import { useEffect, useState } from "react";
import { Search } from "lucide-react";

import { useInventory, useSetRequirement } from "@/api/queries";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Field, Input, Select } from "@/components/Input";
import { useToast } from "@/components/toast-context";
import { formatQuantity, formatUnit } from "@/lib/format";

/**
 * Add one article to a job's list.
 *
 * The picker shows what is *really* available next to each product, because the
 * number someone needs while building a list is not "do we own it" but "can
 * this job have it". Both are shown; only one of them decides.
 */
export function AddRequirementDialog({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}) {
  const toast = useToast();
  const setRequirement = useSetRequirement();

  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [term]);

  const { data: products } = useInventory(
    { q: debounced || undefined, limit: 25 },
    { enabled: open },
  );

  function reset() {
    setTerm("");
    setProductId("");
    setQuantity("");
  }

  async function submit() {
    if (!productId || !quantity) return;
    try {
      await setRequirement.mutateAsync({ projectId, product_id: productId, quantity });
      toast.success("Besoin enregistré");
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Besoin non enregistré", String(error));
    }
  }

  const chosen = products?.items.find((row) => row.product_id === productId);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Ajouter un article"
      description="Un article par ligne : ré-ajouter le même corrige la quantité au lieu de la doubler."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!productId || !quantity || setRequirement.isPending}
          >
            {setRequirement.isPending ? "Enregistrement…" : "Ajouter"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Article">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Nom, fabricant, MPN…"
              className="pl-9"
              aria-label="Chercher un article"
            />
          </div>
          <Select
            value={productId}
            onChange={(event) => setProductId(event.target.value)}
            aria-label="Article"
            className="mt-2"
          >
            <option value="">Choisir un article…</option>
            {products?.items.map((row) => (
              <option key={row.product_id} value={row.product_id}>
                {row.name}
                {row.part_number ? ` (${row.part_number})` : ""} — {formatQuantity(row.available_quantity)}{" "}
                {formatUnit(row.unit)} dispo
              </option>
            ))}
          </Select>
        </Field>

        {chosen && (
          <p className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-[12px] text-ink-muted">
            Physique {formatQuantity(chosen.book_quantity)} · réservé{" "}
            {formatQuantity(chosen.reserved_quantity)} · réellement disponible{" "}
            <span className="text-ink">{formatQuantity(chosen.available_quantity)}</span>{" "}
            {formatUnit(chosen.unit)}
          </p>
        )}

        <Field label="Quantité requise">
          <Input
            type="number"
            min="0"
            step="any"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            placeholder="500"
            aria-label="Quantité requise"
          />
        </Field>
      </div>
    </Dialog>
  );
}
